import DeleteIcon from "@mui/icons-material/Delete"
import NavigateNextIcon from "@mui/icons-material/NavigateNext"
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined"
import { useTheme, CircularProgress, Typography, Stack } from "@mui/material"
import Avatar from "@mui/material/Avatar"
import IconButton from "@mui/material/IconButton"
import {
    ChangeEvent,
    Dispatch,
    ReactElement,
    SetStateAction,
    useState,
} from "react"
import { KeyedMutator } from "swr"

import MfaChip from "./mfaChip"
import {
    DNO_ADMIN,
    DNO_REVIEWER,
    IDENTIFIER_TYPE_DNO_TAG,
    INSTALLER_ADMIN,
    LCT_MANU_ADMIN,
} from "../../../constants/roles"
import {
    TT_ADMIN_SWITCH,
    TT_DELETE_USER_BUTTON,
    TT_FALSE_MFA,
    TT_RESET_MFA_BUTTON,
    TT_REVIWER_SWITCH,
} from "../../../constants/tooltips"
import { useGetUserData } from "../../../hooks/useGetUserData"
import useAddGroup from "../../../services/middleware/hooks/adminPortal/useAddGroup"
import useDeleteUser from "../../../services/middleware/hooks/adminPortal/useDeleteUser"
import useRemoveGroup from "../../../services/middleware/hooks/adminPortal/useRemoveGroup"
import useResetMfa from "../../../services/middleware/hooks/adminPortal/useResetMfa"
import {
    TUserOutput,
    TUsersOutput,
} from "../../../services/middleware/mappers/adminPortal/activeUsersMapper"
import AppButton from "../../buttons/appButton"
import AppSwitch from "../../buttons/switch/appSwitch"
import AppTooltip from "../../helper/appTooltip"
import useConfirmationDialog from "../../popUps/deleteConfirmation/deleteConfirmation"
import DisplayIf from "../../wrappers/displayIf"
import AppTableCell from "../appTableCell"
import AppTableContentRow from "../appTableContentRow"

interface IUserRowProps {
    user: TUserOutput
    mutate: KeyedMutator<TUsersOutput>
    filter: string | null
    mode: string
}

/**
 * Displays each user in a single row
 * <UserRow users={user} />
 */
const UserRow = ({
    user,
    mutate,
    filter,
    mode,
}: IUserRowProps): ReactElement => {
    const theme = useTheme()
    const { handleConfirmation } = useConfirmationDialog()
    const { deleteUser, isLoading: deleteUserLoading } = useDeleteUser(
        user.userName,
        user.fullName,
        mutate
    )
    const { addGroup, isLoading: addGroupLoading } = useAddGroup(
        user.userName,
        user.fullName,
        mutate
    )
    const { removeGroup, isLoading: removeGroupLoading } = useRemoveGroup(
        user.userName,
        user.fullName,
        mutate
    )
    const { resetUserMfa, isLoading: resetMfaLoading } = useResetMfa(
        user.userName,
        user.fullName,
        mutate
    )
    const adminConfig = (): [boolean, string] => {
        if (mode === LCT_MANU_ADMIN) {
            return [user.isLctManuAdmin, LCT_MANU_ADMIN]
        }
        if (mode === INSTALLER_ADMIN) {
            return [user.isInstallerAdmin, INSTALLER_ADMIN]
        }
        if (mode === DNO_ADMIN) {
            return [user.isDnoAdmin, DNO_ADMIN]
        }
        return [user.isInstallerAdmin, DNO_ADMIN]
    }
    const [isAdmin, setIsAdmin] = useState(adminConfig()[0])
    const [dnoReviewer, setDnoReviewer] = useState(user.isDnoReviewer)
    const { isDnoAdmin, isEnaSysAdmin } = useGetUserData()
    const isFalseMfa = user.mfa === false
    const tableCellSx = { opacity: isFalseMfa ? 0.5 : 1 }

    const handleChange = (
        event: ChangeEvent<HTMLInputElement>,
        setSwitchState: Dispatch<SetStateAction<boolean>>,
        group: string
    ): void => {
        const isChecked = event.target.checked
        const switchStateOnFail = !isChecked
        setSwitchState(isChecked)
        if (isChecked) {
            addGroup(group, () => {
                setSwitchState(switchStateOnFail)
            })
        } else {
            removeGroup(group, () => {
                setSwitchState(switchStateOnFail)
            })
        }
    }

    const deleteConfirmation = (): void => {
        handleConfirmation(
            `${user.fullName} will be permanently deleted and removed`,
            deleteUser
        )
    }

    const handleMfaReset = (): void => {
        handleConfirmation(
            `This will reset Multi-Factor Authentication (MFA) for ${user.fullName}. ` +
                `Please ensure they are informed of this change. Upon their next login, they will be required ` +
                `to reset their MFA, after which their existing permissions will be reinstated.`,
            resetUserMfa
        )
    }

    const isRowLoading = (): boolean =>
        addGroupLoading ||
        removeGroupLoading ||
        deleteUserLoading ||
        resetMfaLoading

    const isRowDisabled = (): boolean => isRowLoading() || isFalseMfa

    return (
        <AppTooltip title={user.mfa === true ? "" : TT_FALSE_MFA}>
            <AppTableContentRow>
                <AppTableCell sx={tableCellSx}>
                    {isRowLoading() ? (
                        <CircularProgress />
                    ) : (
                        <Avatar
                            sx={{
                                bgcolor: theme.palette.secondary.main,
                            }}
                        >
                            {user.initials}
                        </Avatar>
                    )}
                </AppTableCell>
                <AppTableCell sx={tableCellSx}>
                    <Stack direction="row">
                        <Typography color="text.primary" marginRight={1}>
                            {user.fullName}
                        </Typography>
                        <DisplayIf condition={isAdmin}>
                            <AppTooltip title="This user is an admin">
                                <ShieldOutlinedIcon
                                    sx={{ color: "text.primary" }}
                                />
                            </AppTooltip>
                        </DisplayIf>
                    </Stack>
                </AppTableCell>
                <AppTableCell sx={tableCellSx}>
                    {" "}
                    <Typography color="text.primary">{user.email}</Typography>
                </AppTableCell>
                <AppTableCell sx={tableCellSx} align="center">
                    <MfaChip isFalseMfa={isFalseMfa} />
                </AppTableCell>
                <AppTableCell align="center">
                    <AppTooltip title={TT_ADMIN_SWITCH}>
                        <span>
                            <AppSwitch
                                checked={isAdmin}
                                onChange={(
                                    event: ChangeEvent<HTMLInputElement>
                                ) =>
                                    handleChange(
                                        event,
                                        setIsAdmin,
                                        adminConfig()[1]
                                    )
                                }
                                disabled={isRowDisabled()}
                            />
                        </span>
                    </AppTooltip>
                </AppTableCell>
                <DisplayIf
                    condition={
                        isDnoAdmin ||
                        (isEnaSysAdmin &&
                            !!filter?.startsWith(IDENTIFIER_TYPE_DNO_TAG))
                    }
                >
                    <AppTableCell align="center">
                        <AppTooltip title={TT_REVIWER_SWITCH}>
                            <span>
                                <AppSwitch
                                    checked={dnoReviewer}
                                    onChange={(
                                        event: ChangeEvent<HTMLInputElement>
                                    ) =>
                                        handleChange(
                                            event,
                                            setDnoReviewer,
                                            DNO_REVIEWER
                                        )
                                    }
                                    disabled={isRowDisabled()}
                                />
                            </span>
                        </AppTooltip>
                    </AppTableCell>
                </DisplayIf>
                <AppTableCell align="center" sx={tableCellSx}>
                    <AppTooltip title={TT_RESET_MFA_BUTTON}>
                        <AppButton
                            onClick={handleMfaReset}
                            variant="contained"
                            endIcon={<NavigateNextIcon />}
                            sx={{
                                padding: "4px 14px",
                                fontSize: "0.75rem",
                                minWidth: "auto",
                                height: "34px",
                                "& .MuiButton-endIcon": {
                                    marginLeft: "2px",
                                },
                                "&.Mui-disabled": {
                                    "@media (prefers-color-scheme: light)": {
                                        backgroundColor: "#BBBBBB",
                                        color: "#666666",
                                    },
                                },
                            }}
                            disabled={isRowDisabled()}
                        >
                            Reset
                        </AppButton>
                    </AppTooltip>
                </AppTableCell>
                <AppTableCell align="center">
                    <AppTooltip title={TT_DELETE_USER_BUTTON}>
                        <IconButton
                            color="error"
                            onClick={deleteConfirmation}
                            disabled={isRowLoading()}
                        >
                            <DeleteIcon />
                        </IconButton>
                    </AppTooltip>
                </AppTableCell>
            </AppTableContentRow>
        </AppTooltip>
    )
}

export default UserRow
